import type { GianoProviderInjection, ChainType } from '../types/giano'
import { concatBytes, padBytes, hexToBytes, bytesToHex, toHex } from 'viem'

import { AuthenticationJSON, RegistrationJSON, parseBase64url, toBase64url } from '@portafino/shared'
import { toPublicKey } from './utils'
import type { PublicKey } from 'ox/PublicKey'
import { PLATFORM_NAME } from '@/constants'
import { CredentialInfo, DecodedUserId, XYVector } from '@appliedblockchain/giano-connector'

const publicKeyMap = new Map<string, PublicKey>()

export interface InjectedGianoProviderOptions {
  credentialName?: string
  challenge?: string
  credentialId?: string
  action?: 'signin' | 'signup'
  /**
   * The smart account this credential signs for, when the caller knows it.
   *
   * Supplied rather than derived — see `BaseCredentialInfo.address`. Absent on
   * registration, where the account does not exist yet.
   */
  address?: `0x${string}`
  onCompleteSigninChallenge?: (credential: AuthenticationJSON, challenge: string) => Promise<{ publicKey: string }>
  onCompleteRegistrationChallenge?: (credential: RegistrationJSON, challenge: string) => Promise<{ address: string }>
}

export const DEFAULT_CREDENTIAL_NAME = `${PLATFORM_NAME} Giano`

export class InjectedGianoProvider implements GianoProviderInjection {
  constructor(private options: InjectedGianoProviderOptions) {}

  async getNameForCredential() {
    return this.options.credentialName || DEFAULT_CREDENTIAL_NAME
  }

  async getCredentialInfo() {
    const { challenge, credentialId, action, address } = this.options
    if (!challenge) {
      throw new Error('Challenge is required')
    }
    const shouldUserPick = action === 'signin' ? 'user-pick' : null
    return {
      credentialId: credentialId ? parseBase64url(credentialId) : shouldUserPick,
      challenge: parseBase64url(challenge),
      // See the portal's implementation: derived addresses assume one passkey per
      // account, which co-ownership ends.
      ...(address ? { address } : {}),
    } as CredentialInfo
  }

  async onCredentialCreated(
    credentialName: string,
    challenge: BufferSource,
    credential: Omit<PublicKeyCredential, 'toJSON'>,
  ) {
    const challengeArray =
      challenge instanceof ArrayBuffer
        ? new Uint8Array(challenge)
        : new Uint8Array(challenge.buffer, challenge.byteOffset, challenge.byteLength)
    const challengeString = toBase64url(challengeArray)

    try {
      const response = credential.response as AuthenticatorAttestationResponse
      const json: RegistrationJSON = {
        type: 'public-key',
        id: credential.id,
        rawId: toBase64url(credential.rawId), // Same as ID, but useful in tests
        authenticatorAttachment: credential.authenticatorAttachment as AuthenticatorAttachment,
        clientExtensionResults: credential.getClientExtensionResults(),
        response: {
          attestationObject: toBase64url(response.attestationObject),
          authenticatorData: toBase64url(response.getAuthenticatorData()),
          clientDataJSON: toBase64url(response.clientDataJSON),
          publicKey: toBase64url(response.getPublicKey()!),
          publicKeyAlgorithm: response.getPublicKeyAlgorithm(),
          transports: response.getTransports() as AuthenticatorTransport[],
        },
        user: {
          name: credentialName,
        },
      }

      const { address } = (await this.options.onCompleteRegistrationChallenge?.(json, challengeString)) || {}
      if (!address) {
        throw new Error("onCompleteRegistrationChallenge didn't return an address")
      }

      return address as `0x${string}`
    } catch (error) {
      console.error('Failed to create passkey', error)
      throw error
    }
  }

  encodeUserId(id: string, gianoSmartWalletFactoryAddress: string, chainId: string, chainType: ChainType) {
    return concatBytes([
      padBytes(hexToBytes(id as `0x${string}`), { size: 16 }),
      padBytes(hexToBytes(gianoSmartWalletFactoryAddress as `0x${string}`), { size: 20 }),
      padBytes(hexToBytes(chainId as `0x${string}`), { size: 4 }),
      padBytes(hexToBytes(chainType.toString(16) as `0x${string}`), { size: 1 }),
    ])
  }

  decodeUserId(userId: Uint8Array) {
    const userIdSlice = userId.slice(0, 16)
    const walletFactoryAddress = userId.slice(16, 36)
    const chainId = userId.slice(36, 40)
    const chainType = userId.slice(40, 41)
    return {
      userId: [
        bytesToHex(userIdSlice.slice(0, 4)),
        bytesToHex(userIdSlice.slice(4, 6)),
        bytesToHex(userIdSlice.slice(6, 8)),
        bytesToHex(userIdSlice.slice(8, 10)),
        bytesToHex(userIdSlice.slice(10)),
      ].join('-'),
      walletFactoryAddress: '0x' + bytesToHex(walletFactoryAddress),
      chainId: parseInt(bytesToHex(chainId), 16),
      chainType: parseInt(bytesToHex(chainType), 16) as ChainType,
    } as DecodedUserId
  }

  async onCredentialSignedIn(credential: PublicKeyCredential) {
    try {
      const response = credential.response as AuthenticatorAssertionResponse
      const json: AuthenticationJSON = {
        type: 'public-key',
        id: credential.id,
        rawId: toBase64url(credential.rawId),
        authenticatorAttachment: credential.authenticatorAttachment as AuthenticatorAttachment,
        clientExtensionResults: credential.getClientExtensionResults(),
        response: {
          clientDataJSON: toBase64url(response.clientDataJSON),
          authenticatorData: toBase64url(response.authenticatorData),
          signature: toBase64url(response.signature),
          userHandle: response.userHandle ? toBase64url(response.userHandle) : undefined,
        },
      }

      const { challenge = '' } = this.options

      const { publicKey } = (await this.options.onCompleteSigninChallenge?.(json, challenge)) || {}
      if (!publicKey) {
        throw new Error("onCompleteSigninChallenge didn't return a public key")
      }

      // Store public key in the map
      publicKeyMap.set(toBase64url(credential.rawId), await toPublicKey(publicKey))

      return true
    } catch (error) {
      console.error('Failed to sign in with passkey', error)
      return false
    }
  }

  async getPublicKeyByCredentialId(rawId: ArrayBuffer) {
    const publicKey = publicKeyMap.get(toBase64url(rawId))

    if (!publicKey) {
      throw new Error('Public key not found')
    }

    return { x: toHex(publicKey.x, { size: 32 }), y: toHex(publicKey.y, { size: 32 }) }
  }

  async onCredentialKey(idHash: ArrayBuffer, xyVector: XYVector) {
    // save the public key to the local storage
    localStorage.setItem(`gpk-${idHash}-public-key`, xyVector.x)
    localStorage.setItem(`gpk-${idHash}-public-key-y`, xyVector.y)
  }
}
